// @flow

import * as React from 'react';
import styled from 'styled-components';
import { SmallText } from '../Text';
import { default as Step } from './Step';
import { Checkmark } from '../svg';

type Props = {
  id: string,
  activeStep: string,
  children: React.ChildrenArray<React.Element<typeof Step>>,
  revealInactive?: boolean,
  title: String,
};

const StyledStepContainer = styled.div`
  width: 2.5rem;
  height: 2.5rem;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledSelectedStep = styled(StyledStepContainer)`
  border-radius: 50%;
  background-color: ${(props) =>
    props.theme.stepIndicator.selectedStep.backgroundColor};
  color: ${(props) => props.theme.stepIndicator.selectedStep.textColor};
  border: 1px solid
    ${(props) => props.theme.stepIndicator.selectedStep.borderColor};
`;

const StyledInactiveStep = styled(StyledStepContainer)`
  border-radius: 50%;
  background-color: ${(props) =>
    props.theme.stepIndicator.inactiveStep.backgroundColor};
  color: ${(props) => props.theme.stepIndicator.inactiveStep.textColor};
  border: 1px solid
    ${(props) => props.theme.stepIndicator.inactiveStep.borderColor};
  cursor: ${(props) => (props.interactive ? 'pointer' : 'default')};
`;

const StyledStep = styled.div`
  width: 1.125rem;
  height: 1.125rem;
  border-radius: 50%;
  background-color: ${(props) => props.theme.disabledColor};
  cursor: ${(props) => (props.interactive ? 'pointer' : 'default')};
`;

const StepTextContainer = styled.div`
  position: absolute;
  top: calc(100% + 0.25rem);
  left: 50%;
  transform: translateX(-50%);
`;

const StepsContainer = styled.ul`
  display: flex;
  flex-direction: 'row';
  justify-content: space-between;
  list-style: none;
  padding: 0;
`;

const StepContainer = styled.li`
  position: relative;
  z-index: 2;
`;

const Track = styled.div`
  width: calc(100% - 2.5em);
  position: absolute;
  top: 50%;
  left: 1.125rem;
  background-color: ${(props) => props.theme.disabledColor};
  height: 1px;
  z-index: 1;
`;

const Container = styled.nav`
  position: relative;
  margin-bottom: 2rem;
`;

const StyledText = styled(SmallText)`
  color: ${(props) =>
    props.selected ? props.theme.textColor : props.theme.disabledColor};
  text-align: center;
  white-space: nowrap;
`;

class StepIndicator extends React.Component<Props> {
  static Step = Step;

  render() {
    return (
      <Container
        {...(this.props.title && {
          'aria-label': this.props.title,
        })}
      >
        <Track />
        <StepsContainer>
          {React.Children.map(this.props.children, (child, i) => {
            const active = this.props.activeStep === child.props.id;
            const interactive = child.props.onClick !== undefined;
            return (
              <StepContainer
                {...(active && { 'aria-current': 'step' })}
                onClick={() =>
                  child.props.onClick && child.props.onClick(child.props.id)
                }
              >
                {active ? (
                  <StyledSelectedStep>{i + 1}</StyledSelectedStep>
                ) : this.props.revealInactive ? (
                  <StyledInactiveStep interactive={interactive}>
                    {child.props.completed ? (
                      <Checkmark width="1.2em" height="1.2em" />
                    ) : (
                      i + 1
                    )}
                  </StyledInactiveStep>
                ) : (
                  <StyledStepContainer>
                    <StyledStep interactive={interactive} />
                  </StyledStepContainer>
                )}
                <StepTextContainer>
                  <StyledText selected={active}>
                    {child.props.children}
                  </StyledText>
                </StepTextContainer>
              </StepContainer>
            );
          })}
        </StepsContainer>
      </Container>
    );
  }
}

export default StepIndicator;
